// @ts-check
/**
 * Ktoré režimy cvičenia má ktorá lekcia v predlohe ATF Online.
 *
 * Zdroj: maska v odpovedi `atfSnap.php` (bit 1 = Hmaty, 2 = Slová, 3 = Vety, 4 = Text),
 * zistená nástrojom `tools/atf-modes.mjs`. Surové dáta sú v `tools/atf-modes.json`.
 *
 * PREČO JE TO DÔLEŽITÉ:
 *  - Lekcia s režimom **Hmaty** preberá nové znaky (drily na znaky).
 *  - Lekcia **bez** Hmaty nové znaky nepreberá — je to opakovanie alebo práca
 *    s hotovým textom (Slová / Vety / Text).
 * Podľa toho sa v našej aplikácii rozhoduje, či sa má generovať dril alebo
 * precvičovať slová a vety. Vďaka tomu sedia aj lekcie, ktoré predloha
 * v ponuke neoznačuje (45–49, 54, 56, 63–66).
 *
 * @type {Record<number, string[]>}
 */
export const REFERENCE_MODES = {
  1: ['Hmaty', 'Slová'],
  2: ['Hmaty', 'Slová'],
  3: ['Hmaty', 'Slová'],
  4: ['Hmaty', 'Slová'],
  5: ['Hmaty', 'Slová', 'Vety'],
  6: ['Hmaty', 'Slová', 'Vety'],
  7: ['Hmaty', 'Slová', 'Vety'],
  8: ['Slová', 'Vety'],
  9: ['Hmaty', 'Slová', 'Vety'],
  10: ['Hmaty', 'Slová', 'Vety'],
  11: ['Hmaty', 'Slová', 'Vety'],
  12: ['Slová', 'Vety', 'Text'],
  13: ['Hmaty', 'Slová', 'Vety'],
  14: ['Hmaty', 'Slová', 'Vety'],
  15: ['Hmaty', 'Slová', 'Vety'],
  16: ['Hmaty', 'Slová', 'Vety'],
  17: ['Slová', 'Vety'],
  18: ['Hmaty', 'Slová', 'Vety'],
  19: ['Hmaty', 'Slová', 'Vety'],
  20: ['Hmaty', 'Slová', 'Vety'],
  21: ['Hmaty', 'Slová', 'Vety'],
  22: ['Slová', 'Vety'],
  23: ['Hmaty', 'Slová', 'Vety'],
  24: ['Hmaty', 'Slová', 'Vety', 'Text'],
  25: ['Hmaty', 'Slová', 'Vety', 'Text'],
  26: ['Hmaty', 'Slová', 'Vety'],
  27: ['Vety', 'Text'],
  28: ['Slová', 'Text'],
  29: ['Slová', 'Vety'],
  30: ['Slová', 'Vety'],
  31: ['Hmaty', 'Slová', 'Vety', 'Text'],
  32: ['Hmaty', 'Slová', 'Vety', 'Text'],
  33: ['Hmaty', 'Slová', 'Vety', 'Text'],
  34: ['Hmaty', 'Slová', 'Vety', 'Text'],
  35: ['Hmaty', 'Slová', 'Vety', 'Text'],
  36: ['Slová', 'Text'],
  37: ['Hmaty', 'Slová', 'Vety', 'Text'],
  38: ['Hmaty', 'Slová', 'Vety', 'Text'],
  39: ['Hmaty', 'Slová', 'Vety', 'Text'],
  40: ['Hmaty', 'Slová', 'Vety', 'Text'],
  41: ['Hmaty', 'Slová', 'Vety', 'Text'],
  42: ['Slová', 'Text'],
  43: ['Hmaty', 'Slová', 'Text'],
  44: ['Hmaty', 'Slová', 'Text'],
  45: ['Slová', 'Text'],
  46: ['Slová'],
  47: ['Slová', 'Vety', 'Text'],
  48: ['Slová', 'Vety', 'Text'],
  49: ['Slová', 'Text'],
  50: ['Slová', 'Vety', 'Text'],
  51: ['Slová'],
  52: ['Slová', 'Text'],
  53: ['Hmaty', 'Text'],
  54: ['Text'],
  55: ['Slová', 'Text'],
  56: ['Text'],
  57: ['Slová', 'Vety', 'Text'],
  58: ['Slová', 'Vety', 'Text'],
  59: ['Slová', 'Text'],
  60: ['Slová', 'Vety', 'Text'],
  61: ['Slová', 'Vety', 'Text'],
  62: ['Slová', 'Vety', 'Text'],
  63: ['Slová', 'Text'],
  64: ['Text'],
  65: ['Slová', 'Text'],
  66: ['Text'],
};

/**
 * Má lekcia v predlohe drily na nové znaky?
 * @param {number} lessonNumber
 */
export function hasDrills(lessonNumber) {
  return (REFERENCE_MODES[lessonNumber] ?? []).includes('Hmaty');
}

/**
 * Má lekcia v predlohe prácu so súvislým textom?
 * @param {number} lessonNumber
 */
export function hasTextPractice(lessonNumber) {
  return (REFERENCE_MODES[lessonNumber] ?? []).includes('Text');
}

/**
 * Štyri režimy cvičenia, presne ako ich ponúka predloha.
 * `slug` je náš interný identifikátor (v URL a v úložisku pokroku),
 * `referenceLabel` je názov v predlohe.
 * @type {Array<{slug: string, label: string, referenceLabel: string, description: string}>}
 */
export const MODES = [
  { slug: 'hmaty', label: 'Hmaty', referenceLabel: 'Hmaty', description: 'Drily na nové znaky a prsty' },
  { slug: 'slova', label: 'Slová', referenceLabel: 'Slová', description: 'Slová zložené len z naučených znakov' },
  { slug: 'vety', label: 'Vety', referenceLabel: 'Vety', description: 'Krátke vety na precvičenie' },
  { slug: 'text', label: 'Text', referenceLabel: 'Text', description: 'Súvislý text' },
];

/** Názov režimu v predlohe → náš identifikátor. */
const SLUG_BY_REFERENCE_LABEL = new Map(MODES.map((mode) => [mode.referenceLabel, mode.slug]));

/**
 * Režimy lekcie ako naše identifikátory, v poradí Hmaty → Slová → Vety → Text.
 * @param {number} lessonNumber
 * @returns {string[]}
 */
export function slugsFor(lessonNumber) {
  const labels = REFERENCE_MODES[lessonNumber] ?? [];
  return MODES.filter((mode) => labels.includes(mode.referenceLabel)).map((mode) => mode.slug);
}

/**
 * Preloží názov režimu z predlohy na náš identifikátor.
 * @param {string} referenceLabel
 */
export function slugForReferenceLabel(referenceLabel) {
  return SLUG_BY_REFERENCE_LABEL.get(referenceLabel) ?? null;
}

/**
 * @param {string} slug
 */
export function modeLabel(slug) {
  return MODES.find((mode) => mode.slug === slug)?.label ?? slug;
}

/** Predvolený režim lekcie: prvý dostupný (drily majú prednosť).
 * @param {number} lessonNumber
 */
export function defaultModeFor(lessonNumber) {
  return slugsFor(lessonNumber)[0] ?? 'slova';
}
